import type { RawAxiosRequestConfig, AxiosError } from 'axios'
import axios from 'axios'
import { Buffer } from 'node:buffer'
import type { Operation } from 'fast-json-patch'
import type { DigitalTwinsModelData } from 'models/DigitalTwinsModelData'
import type { FeatureTwin } from 'models/FeatureTwin'
import type { FeatureQueryTwin } from 'models/FeatureQueryTwin'
import type { DigitalTwinsFile } from 'models/DigitalTwinsFile'
import type { BasicDigitalTwin } from 'models/BasicDigitalTwin'
import type { EnvironmentTwin } from 'models/EnvironmentTwin'
import type { TwinMapping } from 'models/TwinMapping'
import type { ImportMapping } from 'models/ImportMapping'
import type { BatchOrchestrationSubResult, BatchStatus } from './TwinsBatchApiService'
import type { Project } from 'models/Project'
import type { IModelApiService } from 'stores/models'
import { useGlobalStore } from 'stores/global'
import { sortCallback } from 'utils/arrays'
import { getApiToken } from './auth'
import { envBlobContainerClientFactory } from './clients'
import type { RoleRelationship } from './UsersApiService'

export enum EnvImageType {
  image = 'image',
  logo = 'logo'
}

export interface ApiKeyTwin extends BasicDigitalTwin {
  name?: string
  lastUpdateTime?: string
  $roles?: RoleRelationship[]
  $secret?: string
}

export interface SecretTwin extends BasicDigitalTwin {
  name?: string
  secretType?: string
  lastUpdateTime?: string
  $secret?: string
}

export interface SelfSignedCertificateTwin extends BasicDigitalTwin {
  name?: string
  lastUpdateTime?: string
  expiryTime?: string
  thumprint?: string
  // Not in DTDL
  $validityInMonths?: number
}

export interface ArgoApplication {
  spec?: {
    source?: {
      helm: {
        parameters?: { name: string; value: string }[]
      }
    }
  }
  status?: {
    health?: {
      status?: string
      lastTransitionTime?: string
    }
    sync?: {
      status?: string
      revision?: string
    }
    resources?: {
      group?: string
      kind?: string
      name?: string
      namespace?: string
      status?: string
      health?: {
        status?: string
      }
    }[]
  }
}

export interface TemplateInfo {
  name: string
  displayName: string
  description?: string
  template: string
  img?: string
  ontologies?: string[]
  batchTemplate?: string
  kql?: string
  // The model ID for this environment
  model?: string
}

export interface ContactForm {
  name?: string
  email?: string
  company?: string
  idp?: string
  sub?: string
  subject?: string
  environmentId?: string
  message?: string
  toSupport?: boolean
}

/** Result for an environment import job */
export interface EnvironmentImportResult extends BatchStatus {
  put?: BatchOrchestrationSubResult
  mapping?: Record<string, string>
}

class AdminApiService implements IModelApiService {
  private _config: RawAxiosRequestConfig | undefined

  public async getEnvironments() {
    const authResponse = await getApiToken()

    const response = await axios.get<EnvironmentTwin[]>(`${import.meta.env.environmentsApi}`, {
      headers: {
        Authorization: `Bearer ${authResponse?.accessToken}`
      }
    })
    return response.data
  }

  /**
   * Get all role definitions with their permissions from the backend.
   * Used to populate the frontend permission system.
   */
  public async getRoleDefinitions(): Promise<Record<string, string[]>> {
    const authResponse = await getApiToken()

    const response = await axios.get<Record<string, string[]>>(`${import.meta.env.rolesApi}`, {
      headers: {
        Authorization: `Bearer ${authResponse?.accessToken}`
      }
    })
    return response.data
  }

  public async getEnvironment(envId?: string) {
    try {
      const authResponse = await getApiToken()
      const environmentId = envId || useGlobalStore().environmentId
      if (!environmentId) throw new Error('Environment id not defined')
      const response = await axios.get<EnvironmentTwin>(
        import.meta.env.environmentsApi + environmentId,
        {
          headers: {
            Authorization: `Bearer ${authResponse?.accessToken}`,
            'x-adt-id': environmentId
          }
        }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async updateEnvironment(environmentId: string, patch: Operation[]) {
    await this.initialize()

    try {
      await axios.patch(`${import.meta.env.environmentsApi}${environmentId}`, patch, this._config)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  /**
   * Get Environment deployment (Argo Application)
   * @param environmentId environmentId
   */
  public async getEnvironmentDeployment(environmentId: string) {
    try {
      const authResponse = await getApiToken()
      const response = await axios.get<ArgoApplication>(
        `${import.meta.env.environmentsApi}${environmentId}/deployment`,
        {
          headers: {
            Authorization: `Bearer ${authResponse?.accessToken}`
          }
        }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  /**
   * Creates a new environment deployment
   * @param environmentTwin New environmment twin
   */
  public async createEnvironmentDeployment(environmentTwin: EnvironmentTwin) {
    try {
      const authResponse = await getApiToken()
      const response = await axios.post(
        `${import.meta.env.environmentsApi}${environmentTwin.$dtId}/deployment`,
        environmentTwin,
        {
          headers: {
            Authorization: `Bearer ${authResponse?.accessToken}`
          }
        }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  /**
   * Delete Environment deployment (Argo Application)
   * @param environmentId environmentId
   */
  public async deleteEnvironmentDeployment(environmentId: string) {
    await this.initialize()
    try {
      const response = await axios.delete(
        `${import.meta.env.environmentsApi}${environmentId}/deployment`,
        this._config
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  /**
   * Get templates index
   */
  public async getTemplates() {
    try {
      const authResponse = await getApiToken()
      const response = await axios.get<{
        templates: {
          [templateId: string]: TemplateInfo
        }
      }>(`${import.meta.env.environmentsApi}templates`, {
        headers: {
          Authorization: `Bearer ${authResponse?.accessToken}`
        }
      })
      return Object.values(response.data.templates)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  /**
   * Get Environment deployment template content
   * This would probably be a DigitalTwinsFile or a KQL script
   * @param template the id of the template
   */
  public async getTemplateContent(template: string) {
    try {
      const authResponse = await getApiToken()
      const response = await axios.get<DigitalTwinsFile | string>(
        `${import.meta.env.environmentsApi}templates/${template}`,
        {
          headers: {
            Authorization: `Bearer ${authResponse?.accessToken}`
          }
        }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  /**
   * Get Environment image or logo
   * @param {string} envId envId
   * @param {EnvImageType} envImageType image or logo
   */
  public async getEnvironmentImage(envId: string, envImageType: EnvImageType) {
    const envImageContainerClient = envBlobContainerClientFactory(envId, 'images')
    if (!envImageContainerClient) throw new Error('Container client not initialized')
    const blobClient = envImageContainerClient.getBlockBlobClient(envImageType)
    const resp = await blobClient.download()
    return resp.blobBody
  }

  /**
   * Create or update Environment image or logo
   * @param envId The environment ID
   * @param envImageType image or logo
   * @param image image or logo file
   */
  public async createOrUpdateEnvironmentImage(
    envId: string,
    envImageType: EnvImageType,
    image: File
  ) {
    const envImageContainerClient = envBlobContainerClientFactory(envId, 'images')
    if (!envImageContainerClient) throw new Error('Container client not initialized')
    const blobClient = envImageContainerClient.getBlockBlobClient(envImageType)
    return await blobClient.uploadData(image, {
      maxSingleShotSize: 4 * 1024 * 1024,
      blobHTTPHeaders: {
        blobContentType: image.type
      },
      metadata: {
        filename: image.name
      }
    })
  }

  /**
   * Export the entire environment configuration (from admin API)
   * @param environmentId The environment ID
   * @returns
   */
  public async exportEnvironment(environmentId: string) {
    await this.initialize()
    try {
      const response = await axios.get<DigitalTwinsFile>(
        `${import.meta.env.environmentsApi}${environmentId}/export`,
        this._config
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  /**
   * Import the entire environment configuration (from admin API)
   * @param environmentId The environment ID
   * @param digitalTwinsFile The digital twins file
   * @returns
   */
  public async importEnvironment(
    environmentId: string,
    digitalTwinsFile: DigitalTwinsFile,
    options?: { preserveIds?: boolean; overwriteShared?: boolean }
  ) {
    await this.initialize()
    try {
      const response = await axios.post<EnvironmentImportResult>(
        `${import.meta.env.environmentsApi}${environmentId}/import`,
        digitalTwinsFile,
        { ...this._config, params: options }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async getFeatures(
    environmentId?: string,
    options?: { includeQueries?: boolean }
  ): Promise<(FeatureTwin & { $order?: number; $queries?: FeatureQueryTwin[] })[]> {
    const authResponse = await getApiToken()
    const _environmentId = environmentId || useGlobalStore().environmentId
    if (!_environmentId) return []

    const response = await axios.get<
      (FeatureTwin & { $order?: number; $queries?: FeatureQueryTwin[] })[]
    >(`${import.meta.env.featuresApi}`, {
      headers: {
        Authorization: `Bearer ${authResponse?.accessToken}`,
        'x-adt-id': _environmentId
      },
      ...(options?.includeQueries && { params: { includeQueries: true } })
    })
    return response.data?.toSorted(sortCallback)
  }

  public async createOrUpdateFeature(featureId: string, feature: FeatureTwin) {
    await this.initialize()

    try {
      await axios.put<FeatureTwin>(
        `${import.meta.env.featuresApi}${featureId}`,
        feature,
        this._config
      )
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async deleteFeature(featureId: string) {
    await this.initialize()

    try {
      await axios.delete(`${import.meta.env.featuresApi}${featureId}`, this._config)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async shareFeature(featureId: string, targetEnvironmentId: string) {
    await this.shareResource(
      `${import.meta.env.featuresApi}${featureId}/share`,
      targetEnvironmentId
    )
  }

  public async getFeatureQueries(featureId: string): Promise<FeatureQueryTwin[]> {
    await this.initialize()

    const response = await axios.get<FeatureQueryTwin[]>(
      `${import.meta.env.featuresApi}${featureId}/queries`,
      this._config
    )
    return (response.data || []).toSorted(sortCallback)
  }

  public async createOrUpdateFeatureQuery(
    featureId: string,
    queryId: string,
    query: FeatureQueryTwin
  ) {
    await this.initialize()

    try {
      await axios.put(
        `${import.meta.env.featuresApi}${featureId}/queries/${queryId}`,
        query,
        this._config
      )
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async updateFeatureQuery(featureId: string, queryId: string, patch: Operation[]) {
    await this.initialize()

    try {
      await axios.patch(
        `${import.meta.env.featuresApi}${featureId}/queries/${queryId}`,
        patch,
        this._config
      )
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async deleteFeatureQuery(featureId: string, queryId: string) {
    await this.initialize()

    try {
      await axios.delete(
        `${import.meta.env.featuresApi}${featureId}/queries/${queryId}`,
        this._config
      )
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async getModels() {
    // Without a token there is nothing to authorise the request with: bail out
    // instead of falling through to a call carrying no (or a stale) token.
    if (!(await this.initialize())) return undefined

    const response = await axios.get<DigitalTwinsModelData[]>(
      `${import.meta.env.environmentsApi}models`,
      {
        headers: {
          Authorization: this._config?.headers?.Authorization
        }
      }
    )
    return response.data
  }

  public async getImportMappings() {
    await this.initialize()

    const { environmentId } = useGlobalStore()
    const response = await axios.get<ImportMapping[]>(
      `${import.meta.env.environmentsApi}${environmentId}/import-mappings`,
      this._config
    )
    return response.data
  }

  public async createOrUpdateImportMapping(importMappingId: string, importMapping: ImportMapping) {
    await this.initialize()
    try {
      const { environmentId } = useGlobalStore()
      const response = await axios.put<ImportMapping>(
        `${import.meta.env.environmentsApi}${environmentId}/import-mappings/${importMappingId}`,
        importMapping,
        this._config
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async deleteImportMapping(importMappingId: string) {
    await this.initialize()

    try {
      const { environmentId } = useGlobalStore()
      await axios.delete(
        `${import.meta.env.environmentsApi}${environmentId}/import-mappings/${importMappingId}`,
        this._config
      )
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async shareImportMapping(importMappingId: string, targetEnvironmentId: string) {
    const { environmentId } = useGlobalStore()
    await this.shareResource(
      `${import.meta.env.environmentsApi}${environmentId}/import-mappings/${importMappingId}/share`,
      targetEnvironmentId
    )
  }

  public async getImportMappingTwinMappings(importMappingId: string) {
    await this.initialize()

    const { environmentId } = useGlobalStore()
    const response = await axios.get<TwinMapping[]>(
      `${import.meta.env.environmentsApi}${environmentId}/import-mappings/${importMappingId}/mappings`,
      this._config
    )
    return response.data?.toSorted(sortCallback)
  }

  public async createOrUpdateImportMappingTwinMapping(
    importMappingId: string,
    twinMappingId: string,
    twinMapping: TwinMapping
  ) {
    await this.initialize()
    try {
      const { environmentId } = useGlobalStore()
      const response = await axios.put<TwinMapping>(
        `${import.meta.env.environmentsApi}${environmentId}/import-mappings/${importMappingId}/mappings/${twinMappingId}`,
        twinMapping,
        this._config
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async deleteImportMappingTwinMapping(importMappingId: string, twinMappingId: string) {
    await this.initialize()

    try {
      const { environmentId } = useGlobalStore()
      await axios.delete(
        `${import.meta.env.environmentsApi}${environmentId}/import-mappings/${importMappingId}/mappings/${twinMappingId}`,
        this._config
      )
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async getSecrets() {
    await this.initialize()
    const response = await axios.get<SecretTwin[]>(`${import.meta.env.secretsApi}`, this._config)
    return response.data
  }

  public async getSecretsByType(authenticationType: string) {
    await this.initialize()
    const response = await axios.get<SecretTwin[]>(
      `${import.meta.env.secretsApi}?type=${encodeURIComponent(authenticationType)}`,
      this._config
    )
    return response.data
  }

  public async getSecret(secretId: string) {
    await this.initialize()

    const response = await axios.get<SecretTwin>(
      `${import.meta.env.secretsApi}${secretId}`,
      this._config
    )
    return response.data
  }

  public async createOrUpdateSecret(secretId: string, secretTwin: SecretTwin) {
    await this.initialize()

    try {
      const rawSecret = secretTwin.$secret
      if (!rawSecret) throw new Error('Secret value is required')
      // Convert to base64
      const secretBase64 = Buffer.from(rawSecret).toString('base64')
      if (!secretBase64) throw new Error('Failed to encode secret to base64')
      const secretBody = {
        ...secretTwin,
        $secret: secretBase64 // replace $secret with base64 value
      }
      const response = await axios.put<SecretTwin>(
        `${import.meta.env.secretsApi}${secretId}`,
        secretBody,
        this._config
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async updateSecret(secretId: string, patch: Operation[]) {
    await this.initialize()

    try {
      await axios.patch(`${import.meta.env.secretsApi}${secretId}`, patch, this._config)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async deleteSecret(secretId: string) {
    await this.initialize()

    try {
      await axios.delete(`${import.meta.env.secretsApi}${secretId}`, this._config)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async shareSecret(secretId: string, targetEnvironmentId: string) {
    await this.shareResource(`${import.meta.env.secretsApi}${secretId}/share`, targetEnvironmentId)
  }

  public async getApiKeys() {
    await this.initialize()

    const response = await axios.get<ApiKeyTwin[]>(`${import.meta.env.apiKeysApi}`, this._config)
    return response.data
  }

  public async getApiKey(apiKeyId: string) {
    await this.initialize()

    const response = await axios.get<ApiKeyTwin>(
      `${import.meta.env.apiKeysApi}${apiKeyId}`,
      this._config
    )
    return response.data
  }

  public async createOrUpdateApiKey(apiKeyId: string, apiKeyTwin: ApiKeyTwin) {
    await this.initialize()
    try {
      const response = await axios.put<ApiKeyTwin>(
        `${import.meta.env.apiKeysApi}${apiKeyId}`,
        apiKeyTwin,
        this._config
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async updateApiKey(apiKeyId: string, patch: Operation[]) {
    await this.initialize()

    try {
      await axios.patch(`${import.meta.env.apiKeysApi}${apiKeyId}`, patch, this._config)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async deleteApiKey(apiKeyId: string) {
    await this.initialize()

    try {
      await axios.delete(`${import.meta.env.apiKeysApi}${apiKeyId}`, this._config)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async shareApiKey(apiKeyId: string, targetEnvironmentId: string) {
    await this.shareResource(`${import.meta.env.apiKeysApi}${apiKeyId}/share`, targetEnvironmentId)
  }

  public async getCertificates() {
    await this.initialize()

    const response = await axios.get<SelfSignedCertificateTwin[]>(
      `${import.meta.env.certificatesApi}`,
      this._config
    )
    return response.data
  }

  public async getCertificate(certificateId: string) {
    await this.initialize()

    const response = await axios.get<SelfSignedCertificateTwin>(
      `${import.meta.env.certificatesApi}${certificateId}`,
      this._config
    )
    return response.data
  }

  public async createOrUpdateCertificate(
    certificateId: string,
    certificate: SelfSignedCertificateTwin
  ) {
    await this.initialize()

    try {
      const response = await axios.put<SelfSignedCertificateTwin>(
        `${import.meta.env.certificatesApi}${certificateId}`,
        certificate,
        this._config
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async updateCertificate(certificateId: string, patch: Operation[]) {
    await this.initialize()

    try {
      await axios.patch(`${import.meta.env.certificatesApi}${certificateId}`, patch, this._config)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async deleteCertificate(certificateId: string) {
    await this.initialize()

    try {
      await axios.delete(`${import.meta.env.certificatesApi}${certificateId}`, this._config)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async downloadCertificate(certificateId: string, format: 'pem' | 'pfx' = 'pem') {
    await this.initialize()
    const response = await axios.get<Blob>(
      `${import.meta.env.certificatesApi}${certificateId}/download${format === 'pem' ? '-pem' : ''}`,
      { ...this._config, responseType: 'blob' }
    )
    return response.data
  }

  public async sendContactMail(form: ContactForm) {
    try {
      await axios.post(`${import.meta.env.environmentsApi}contact`, form)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  /** Gets a token for an Azure resource that's available to all Digital Twins users. Currently only supports Azure Maps. */
  public async getToken(resource: string) {
    await this.initialize()
    try {
      const response = await axios.get(`${import.meta.env.tokenServiceEndpoint}`, {
        params: { resource },
        ...this._config
      })
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }
  public async getContainerSas() {
    await this.initialize()
    try {
      const response = await axios.get<{ sas: string }>(
        `${import.meta.env.sasEndpoint}`,
        this._config
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async getBlobSas(blobId: string, expiryHours?: number) {
    await this.initialize()
    try {
      const response = await axios.get<{ sas: string; url: string }>(
        `${import.meta.env.sasEndpoint}`,
        {
          params: { blob: blobId, ...(expiryHours !== undefined && { expiryHours }) },
          ...this._config
        }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async getSharedBlobSas(sharedBlobId: string) {
    await this.initialize()
    try {
      const response = await axios.get<{ sas: string; url: string }>(
        `${import.meta.env.sasEndpoint}`,
        {
          params: { sharedBlob: sharedBlobId },
          ...this._config
        }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async signExternalUrl(href: string) {
    await this.initialize()
    try {
      const response = await axios.get<{ href: string; expiry?: string }>(
        `${import.meta.env.sasEndpoint}`,
        {
          params: { href },
          ...this._config
        }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async downloadOneLakeFile(lakehouseId: string, path: string) {
    await this.initialize()
    try {
      const response = await axios.get<Blob>(
        `${import.meta.env.onelakeProxy}${lakehouseId}/Files/${path}`,
        { ...this._config, responseType: 'blob' }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async getOneLakeSas(lakehouseId: string, path?: string) {
    await this.initialize()
    try {
      const response = await axios.get<{ sas: string; url?: string }>(
        `${import.meta.env.sasEndpoint}`,
        {
          params: { lakehouseId, ...(path && { path }) },
          ...this._config
        }
      )
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  public async getProject(projectId: string) {
    try {
      const authResponse = await getApiToken()
      const response = await axios.get<Project>(`${import.meta.env.projectsApi}${projectId}`, {
        headers: {
          Authorization: `Bearer ${authResponse?.accessToken}`
        }
      })
      return response.data
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  private async shareResource(url: string, targetEnvironmentId: string) {
    await this.initialize()

    try {
      await axios.post(url, { targetEnvironmentId }, this._config)
    } catch (e) {
      console.error(e)
      throw this.createError(e)
    }
  }

  /**
   * Resolves the shared axios config (auth header + environment id).
   *
   * Returns `false` when no access token is available, which callers MUST check:
   * the early return leaves `_config` untouched, and because this is a singleton
   * service that stale value may still carry an expired bearer token, so a caller
   * that ignores the result silently issues an unauthenticated request.
   */
  private async initialize(): Promise<boolean> {
    const authResponse = await getApiToken()
    if (!authResponse?.accessToken) return false
    const { environmentId } = useGlobalStore()
    if (!environmentId) throw new Error('Environment id not found')
    this._config = {
      headers: {
        Authorization: 'Bearer ' + authResponse.accessToken,
        'x-adt-id': environmentId
      }
    }
    return true
  }

  private createError(error: unknown) {
    if (error instanceof Error) {
      const _error = new Error(error.message)
      const _errorAxios = error as AxiosError
      if (_errorAxios.isAxiosError) {
        const errData = _errorAxios?.response?.data as {
          code?: string
          message?: string
          error?: string
        }
        if (errData.code) _error.name = errData.code
        if (errData.message) _error.message = errData?.message
        else if (errData.error) _error.message = errData?.error
        else _error.message = _errorAxios.message
      }
      return _error
    }
  }
}

export const adminApiService = new AdminApiService()
export const envApiService = adminApiService
